/*
 * Edit Shopping Cart Form
 */

import React from 'react';
import { Button, Col, FormLabel, FormControl, Card, Row } from 'react-bootstrap';
import { FieldConfig, FieldGroup, FormBuilder } from 'react-reactive-form';
import { toastr } from 'react-redux-toastr';

import { RouteComponentProps } from 'react-router-dom';
import { addPartToCart, deleteQuoteItemFromQuoteCart } from '../../../actions/partsActions';
import { clearCart, deleteFromCart, saveMessageInput, updateQuantityCart } from '../../../actions/shoppingCartActions';
import { constants } from '../../../constants/constants';
import { Ipart, IshoppingCartGroupedItem, IshoppingCartItem, IshoppingCartWithGroupedItems } from '../../../models';
import { partTypeEnum } from '../../../models-enums';
import { SearchAllPartsForm } from '../../manageJob/SearchAllPartsForm';
import { FormUtil } from '../FormUtil';
import { ShoppingCartSubSubItem } from './ShoppingCartSubSubItem';
import { TFunction } from 'react-i18next';

export enum suggestedPartsQueryParamsEnum {
    showSearchPartsModal = 'showSearchPartsModal'
}
export interface ImetaNumberInputWithButton {
    label: string;
    placeholder?: string;
    buttonAction: (id: string) => void;
    id: string;
}
interface Iprops extends RouteComponentProps<any> {
    onSubmit: (message: string, items: IshoppingCartItem[]) => void;
    loading: boolean;
    colorButton: string;
    t: TFunction;
    selectedCart: IshoppingCartWithGroupedItems;
    updateQuantityCart: typeof updateQuantityCart;
    saveMessageInput: typeof saveMessageInput;
    deleteFromCart: typeof deleteFromCart;
    cartName: string;
    showCost?: boolean;
    addPartToCart: typeof addPartToCart;
    deleteQuoteItemFromQuoteCart: typeof deleteQuoteItemFromQuoteCart;
    clearCart: typeof clearCart;
    isMobile: boolean;
}
interface Istate {
    fieldConfig: FieldConfig;
    interalComments: string;
    localCart: IshoppingCartWithGroupedItems;
}

export class ShoppingCartForm extends React.Component<Iprops, Istate> {
    private subscription: any;
    private colorButton = constants.colors[`blueButton`];

    // Custom breakpoint for shopping cart layout (larger than standard mobile breakpoint)
    private shouldStackColumns = (): boolean => {
        const { isMobile } = this.props;
        // Stack columns if mobile OR window width is less than 1200px
        if (isMobile) return true;
        if (typeof window !== 'undefined') {
            return window.innerWidth < 1200;
        }
        return false;
    };

    private handleLaborAdd = (part: Ipart) => {
        const matchPart = this.state.localCart.laborItems.find(
            item => item.name.includes(part.number) && parseInt(`${item.bundle}`) === part?.bundle
        );

        if (part.quantity === undefined) {
            part.quantity = 1;
        }

        if (matchPart) {
            toastr.error(this.props.t('toastMessage:duplicatedLaborPartBundle'), '', constants.toastrError);
        } else {
            this.props.addPartToCart(part, undefined, part?.bundle);
        }
    };

    constructor(props: Iprops) {
        super(props);

        this.state = {
            fieldConfig: { controls: {} },
            interalComments: this.props.selectedCart.message,
            localCart: this.props.selectedCart
        };
    }

    // TODO keep the message somewhere, patch the value
    componentDidMount() {
        // this.setFormConfig();
    }

    componentWillUnmount() {
        // TODO either subscribe to the cart changes and don't do anything here
        // or call something like:
        // this.props.onSubmit(this.formGroup.value.message, this.formGroup.value);
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    componentDidUpdate(prevProps: Iprops) {
        if (
            prevProps.selectedCart.addedIDs.length !== this.props.selectedCart.addedIDs.length ||
            prevProps.selectedCart.laborItems.length !== this.props.selectedCart.laborItems.length
        ) {
            this.setState({ localCart: this.props.selectedCart });
        }
    }

    formGroup = FormBuilder.group({});

    handleDeleteCartItem = (id: string, cartName: string) => {
        this.props.deleteQuoteItemFromQuoteCart(id);
        this.props.deleteFromCart(id, cartName);
    };

    handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error(this.props.t('toastMessage:invalidFormSubmission'), '', constants.toastrError);
            return;
        }

        this.props.onSubmit(this.state.interalComments, this.formValuesToItem(true));
        this.props.clearCart();
        this.props.history.goBack();
    };

    formValuesToItem = (updateQuantity = false): IshoppingCartItem[] => {
        const shoppingCartItems = Object.keys(this.formGroup.value).reduce(
            (items: IshoppingCartItem[], quotePartID) => {
                const item = FormUtil.getValues(this.formGroup.value[quotePartID]);

                if (updateQuantity) {
                    let tempQuantity: number = parseInt(item.quantity as string);
                    this.props.updateQuantityCart(tempQuantity, quotePartID, this.props.cartName);
                }
                return [
                    ...items,
                    {
                        id: quotePartID,
                        name: '',
                        quantity: item.quantity,
                        bundle: item.bundle
                    } as IshoppingCartItem
                ];
            },
            []
        );
        return shoppingCartItems;
    };

    handleEmptySubmit = () => {
        this.props.onSubmit('', []);
    };

    handleInputChange(event: any) {
        const { value } = event.currentTarget;
        this.setState({ interalComments: value });
    }

    render() {
        const { t, selectedCart } = this.props;
        const formClassName = `clearfix beacon-form manage-form ${this.props.colorButton}`;
        const shouldStack = this.shouldStackColumns();

        if (selectedCart.addedIDs.length === 0) {
            const emptyQuoteMessage = this.props.t('partCartModal.emptyQuoteMessage');
            const submitButtonText = this.props.t('partCartModal.submitButton');
            return (
                <div>
                    <h4 style={{ padding: '15px' }}>{emptyQuoteMessage}</h4>
                    <Col xs={12} className="form-buttons text-right">
                        <Button
                            variant={this.props.colorButton}
                            type="button"
                            onClick={() => this.handleEmptySubmit()}
                            disabled={this.props.loading}
                        >
                            {submitButtonText}
                        </Button>
                    </Col>
                </div>
            );
        }

        return (
            <FieldGroup
                control={this.formGroup}
                strict={false}
                render={({ get, invalid, reset, value }) => (
                    <form onSubmit={this.handleSubmit} className={formClassName}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: shouldStack ? 'column' : 'row',
                                flexWrap: shouldStack ? 'wrap' : 'nowrap',
                                gap: shouldStack ? '0' : '15px'
                            }}
                        >
                            <div
                                style={{
                                    flex: shouldStack ? '1 1 100%' : '1 1 0',
                                    minWidth: shouldStack ? 'auto' : '400px'
                                }}
                            >
                                {this.state.localCart.groupedItems.map(
                                    (item: IshoppingCartGroupedItem, index: number) => {
                                        // PRODUCT
                                        return (
                                            <Card key={item.name}>
                                                <Card.Header className="shopping-cart-header">{item.name}</Card.Header>
                                                <Card.Body>
                                                    <Row>
                                                        <Col xs={5}>{String(t('partCartModal.asset'))}</Col>
                                                        <Col xs={3}>{String(t('partCartModal.bundle'))}</Col>
                                                        <Col xs={4}>{String(t('partCartModal.quantity'))}</Col>
                                                    </Row>

                                                    {item.subItems.map(subItem => {
                                                        // INSTALL BASE
                                                        return (
                                                            <Row key={subItem.name}>
                                                                {subItem.subSubItems.map(subSubItem => (
                                                                    // PART
                                                                    <ShoppingCartSubSubItem
                                                                        subItemName={subItem.name}
                                                                        subSubItem={subSubItem}
                                                                        key={subSubItem.id}
                                                                        deleteAction={this.handleDeleteCartItem}
                                                                        cartName={this.props.cartName}
                                                                        main
                                                                    />
                                                                ))}
                                                            </Row>
                                                        );
                                                    })}
                                                </Card.Body>
                                            </Card>
                                        );
                                    }
                                )}
                            </div>
                            <div
                                style={{
                                    flex: shouldStack ? '1 1 100%' : '1 1 0',
                                    minWidth: shouldStack ? 'auto' : '400px',
                                    marginTop: shouldStack ? '15px' : '0'
                                }}
                            >
                                <Card>
                                    <Card.Header className="shopping-cart-header">
                                        {String(t('laborTravel'))}
                                    </Card.Header>
                                    <Card.Body>
                                        <div style={{ width: '100%', overflow: 'hidden' }}>
                                            <div className="labor-search-constrained">
                                                <SearchAllPartsForm
                                                    partType={partTypeEnum.labor}
                                                    colorButton={this.colorButton}
                                                    onSubmit={this.handleLaborAdd}
                                                    t={t}
                                                    loading={this.props.loading}
                                                    shouldShowQuote
                                                    shouldShowQuantity
                                                />
                                            </div>
                                        </div>
                                        <Row style={{ paddingTop: '20px' }}>
                                            <Col xs={5}>{String(t('partCartModal.asset'))}</Col>
                                            <Col xs={3}>{String(t('partCartModal.bundle'))}</Col>
                                            <Col xs={4}>{String(t('partCartModal.quantity'))}</Col>
                                        </Row>
                                        {this.state.localCart.laborItems.map((laborItem: any, laborIndex: number) => {
                                            return (
                                                <Row key={laborItem.id} style={{ marginBottom: '10px' }}>
                                                    <ShoppingCartSubSubItem
                                                        subSubItem={laborItem}
                                                        deleteAction={this.handleDeleteCartItem}
                                                        cartName={this.props.cartName}
                                                    />
                                                </Row>
                                            );
                                        })}
                                    </Card.Body>
                                </Card>

                                <Card style={{ marginTop: '15px' }}>
                                    <Card.Header className="shopping-cart-header">
                                        {String(t('partCartModal.internalComments'))}
                                    </Card.Header>
                                    <Card.Body>
                                        <FormControl
                                            as="textarea"
                                            type="text"
                                            rows={shouldStack ? 4 : 6}
                                            value={this.state.interalComments}
                                            onChange={event => {
                                                this.handleInputChange(event);
                                            }}
                                            style={{ minHeight: shouldStack ? '50px' : '100px' }}
                                        ></FormControl>
                                        <FormLabel style={{ marginTop: '3px' }}>
                                            {String(t('cannotCompleteCommentLabel'))}
                                        </FormLabel>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                marginTop: '15px',
                                marginBottom: '15px'
                            }}
                        >
                            <Button
                                onClick={() => {
                                    this.props.history.goBack();
                                }}
                            >
                                {String(t('backButton'))}
                            </Button>
                            <Button variant={this.props.colorButton} type="submit" disabled={this.props.loading}>
                                {String(t('partCartModal.submitButton'))}
                            </Button>
                        </div>
                    </form>
                )}
            />
        );
    }
}
